'use client';

import { useEffect, useState } from 'react';
import { FileText, Trash2, Eye, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  getCustomerDocuments,
  downloadDocument,
  deleteDocument,
  getDocumentTypeLabel,
  getUploadStatusLabel,
  getUploadStatusColor,
  formatFileSize,
  type KycDocument,
} from '@/lib/api/kyc-documents';

/**
 * Fix filename encoding issues (UTF-8 mojibake)
 * Handles cases where Thai characters appear as garbled text like "à¸ à¸²à¸à¸"
 */
function fixFilenameEncoding(filename: string): string {
  if (!filename) return '';

  // Try to detect and fix UTF-8 mojibake
  try {
    // If the string contains mojibake patterns (multiple consecutive high bytes)
    if (/[\xE0-\xFF][\x80-\xBF]/.test(filename)) {
      // Attempt to re-encode as UTF-8
      const buffer = Buffer.from(filename, 'latin1');
      const decodedFilename = buffer.toString('utf8');
      
      // Only use the decoded version if it contains Thai characters or looks valid
      if (/[\u0E00-\u0E7F]/.test(decodedFilename)) {
        return decodedFilename;
      }
    }
  } catch (error) {
    console.warn('Error fixing filename encoding:', error);
  }

  return filename;
}

interface DocumentListProps {
  documentType?: string;
  onRefresh?: () => void;
  onDocumentDeleted?: (documentType: string) => void;
  showActions?: boolean;
}

export default function DocumentList({
  documentType,
  onRefresh,
  onDocumentDeleted,
  showActions = true,
}: DocumentListProps) {
  const [documents, setDocuments] = useState<KycDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<KycDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getCustomerDocuments(documentType);
      setDocuments(response.data);
    } catch (err: any) {
      setError(err.message || 'ไม่สามารถโหลดรายการเอกสารได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [documentType]);

  const handleDownload = async (document: KycDocument) => {
    try {
      await downloadDocument(document.id);
    } catch (err: any) {
      setError(err.message || 'ไม่สามารถดาวน์โหลดเอกสารได้');
    }
  };

  const handleDeleteClick = (document: KycDocument) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setDeleting(true);
    try {
      const deletedDocType = documentToDelete.document_type;
      await deleteDocument(documentToDelete.id);
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      fetchDocuments(); // Refresh list
      if (onRefresh) onRefresh();
      if (onDocumentDeleted) onDocumentDeleted(deletedDocType);
    } catch (err: any) {
      setError(err.message || 'ไม่สามารถลบเอกสารได้');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'pending':
      case 'uploaded':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600">กำลังโหลดเอกสาร...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-2" />
            <p>ยังไม่มีเอกสารที่อัพโหลด</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {documents.map((document) => (
          <Card key={document.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  {/* Status Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getStatusIcon(document.upload_status)}
                  </div>

                  {/* Document Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {getDocumentTypeLabel(document.document_type)}
                      </h4>
                      <Badge className={getUploadStatusColor(document.upload_status)}>
                        {getUploadStatusLabel(document.upload_status)}
                      </Badge>
                    </div>

                    <p className="text-xs text-gray-600 truncate mb-1 max-w-xs">
                      {fixFilenameEncoding(document.original_file_name)}
                    </p>

                    <div className="flex items-center space-x-3 text-xs text-gray-500 flex-wrap">
                      <span>{formatFileSize(document.file_size)}</span>
                      <span>•</span>
                      <span>อัพโหลดเมื่อ {new Date(document.uploaded_at).toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}</span>
                    </div>

                    {/* Verified Info */}
                    {document.verified_at && (
                      <p className="text-xs text-green-600 mt-1">
                        ตรวจสอบแล้วเมื่อ {new Date(document.verified_at).toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    )}

                    {/* Rejection Info */}
                    {document.rejected_at && document.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-xs text-red-800 break-words">
                          <strong>เหตุผลที่ปฏิเสธ:</strong> {document.rejection_reason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {showActions && (
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(document)}
                      title="ดูเอกสาร"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>

                    {document.upload_status !== 'verified' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteClick(document)}
                        title="ลบเอกสาร"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบเอกสาร</AlertDialogTitle>
            <AlertDialogDescription>
              คุณแน่ใจหรือไม่ว่าต้องการลบเอกสาร "{documentToDelete?.original_file_name}"?
              <br />
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  กำลังลบ...
                </>
              ) : (
                'ลบเอกสาร'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
